import type { AirspaceNotamItem, AirspaceTfrItem } from '@/components/dashboard/AirspacePanel';
import { STYLES, type ContextMenu, type MapDispatchMenu, type MapStyleKey, type TriggerCard } from './droneMapTypes';
import {
  isSatelliteAvailable,
  readStoredMapStyle,
  retireLegacyMapStyleKeys,
  writeStoredMapStyle,
} from '@/lib/basemap-preference';

/** The basemap with no operator choice behind it on a server with no imagery. Also what
 *  the SERVER renders, where there is no localStorage to consult - the client's first
 *  render must agree with it, which is what `hydrated` gates in `DroneMap`. */
export const FALLBACK_MAP_STYLE: MapStyleKey = 'dark';

/** The basemap a profile with no choice of its own opens on: imagery wherever this
 *  deployment can serve it, else the dark vector map. Independent of DAY/NIGHT. */
export function defaultMapStyle(): MapStyleKey {
  return isSatelliteAvailable() ? 'satellite' : FALLBACK_MAP_STYLE;
}

/**
 * The basemap this browser opens on: the operator's own choice, else the deployment's
 * default. Takes no display mode, deliberately - see `MAP_STYLE_STORAGE_KEY`.
 *
 * A stored value naming a style we no longer have falls through to the default rather
 * than reaching MapLibre, which is why the raw read is validated against `STYLES` here
 * rather than in the storage module.
 */
// Once per module load, not once per call. This is a read path taken by six map
// constructors and every reducer init; three `removeItem` writes on each of them
// is work done thousands of times to delete keys that are gone after the first.
if (typeof window !== 'undefined') retireLegacyMapStyleKeys();

export function getPersistedMapStyle(): MapStyleKey {
  if (typeof window === 'undefined') return FALLBACK_MAP_STYLE;
  const saved = readStoredMapStyle();
  return saved && saved in STYLES ? (saved as MapStyleKey) : defaultMapStyle();
}

/** The operator chose a basemap; it is theirs in both display modes, until they change it. */
export function persistMapStyle(style: MapStyleKey): void {
  writeStoredMapStyle(style);
}

export type StyleRequestOutcome = 'apply' | 'defer' | 'noop';

/**
 * What a basemap request does while a style load may be in flight (build record
 * D14). A button press that lands mid-load is dropped: the operator can see the
 * button and presses again. An AUTOMATIC request - the move onto this deployment's
 * default once `/api/auth/me` says imagery can be served - is deferred until the
 * load finishes, because nothing will ask again on the operator's behalf and a
 * dropped one leaves the operator on a basemap nobody chose.
 */
export function styleRequestOutcome(
  loading: boolean,
  current: MapStyleKey,
  next: MapStyleKey,
  automatic: boolean,
): StyleRequestOutcome {
  if (next === current) return 'noop';
  if (!loading) return 'apply';
  return automatic ? 'defer' : 'noop';
}

/** Drone under the cursor, with the screen point to anchor its chip to. */
export interface HoveredDrone {
  droneId: string;
  x: number;
  y: number;
  /** Canvas width at hover time, so the chip can keep itself in bounds without
   *  reading a ref during render. */
  containerWidth: number;
}

export interface MapUiState {
  isFullscreen: boolean;
  mapStyle: MapStyleKey;
  contextMenu: ContextMenu | null;
  mapDispatchMenu: MapDispatchMenu | null;
  /** An external trigger's card, anchored at the point the operator clicked. */
  triggerCard: TriggerCard | null;
  cursorCoords: string | null;
  /** Only the id and position are held here; the chip reads live telemetry from
   *  the drones prop at render, so it never shows a frozen snapshot. */
  hoveredDrone: HoveredDrone | null;
  trailDroneIds: Set<string>;
  controllerLinkId: string | null;
  followDroneId: string | null;
  stalenessTick: number;
  /**
   * Wall clock at the last staleness tick, for anything that has to judge
   * freshness DURING RENDER.
   *
   * `stalenessTick` is a counter and is enough to key an effect, but a render
   * cannot call `Date.now()` - it is impure, and the React Compiler rejects it.
   * This rides on the same one-second tick the markers and the strip board use,
   * so a link that goes LOST is seen by the drawn symbol and by the controls
   * that talk about it at the same moment rather than one behind the other.
   *
   * Zero until the first tick, which reads as "nothing is stale" (a negative age
   * is level 0). That is the safe direction for the one second after mount: a
   * control is offered rather than refused.
   */
  stalenessNowMs: number;
  /**
   * The camera is off north-up: rotated in bearing, tilted in pitch, or both.
   *
   * Held here rather than read from the map at render because MapLibre's camera
   * is not React state - nothing re-renders when it moves. It is what decides
   * whether the Reset north key is drawn at all (D11: a control that cannot act
   * is not rendered), and on a map that is north-up, which is nearly all of the
   * time, that key does nothing.
   */
  mapRotated: boolean;
  showAirspace: boolean;
  /**
   * Planned mission routes - the polylines and numbered waypoints of every mission
   * in view, not only the one an operator is working.
   *
   * Defaults OFF, with `showAirspace`, and for the same reason: it paints on the
   * resting picture without being asked. A fleet with a few missions planned draws
   * a route across the map before anybody has selected anything, and what an air
   * picture should show at rest is where the aircraft ARE. Turning it on is one
   * item in the Layers menu; the routes also come back on their own when the
   * operator picks a mission (`selectedMissionId` re-enables the layer).
   */
  showMissions: boolean;
  showAltitudeLabels: boolean;
  /**
   * Range rings around the selected airborne aircraft, plus the transit leg to a
   * hovered/picked mission's first waypoint.
   *
   * Defaults ON, unlike `showAirspace`. The two are not comparable: airspace
   * paints polygons across the whole viewport unconditionally, while this draws
   * NOTHING until an operator picks an airborne aircraft - the resting picture
   * is byte-for-byte unchanged. A toggle is still offered because an operator
   * working a dense area must be able to take the ink back.
   */
  showRangeRings: boolean;
  /** True when the base map CDN was unreachable and we fell back to the plain
   *  offline style - drives the visible "basemap offline" indicator. */
  basemapOffline: boolean;
  /** True while a basemap load attempt is in flight (initial, automatic retry,
   *  or operator-triggered). Drives the indicator's "retrying" state so the
   *  retry control reports what it is actually doing. */
  basemapRetrying: boolean;
  tfrItems: AirspaceTfrItem[];
  notamItems: AirspaceNotamItem[];
  notamClearToFly: boolean;
  loadingAirspace: boolean;
  airspaceErr: string | null;
}

export type MapUiAction =
  | { type: 'SET_FULLSCREEN'; value: boolean }
  | { type: 'SET_MAP_STYLE'; value: MapStyleKey }
  | { type: 'SET_CONTEXT_MENU'; value: ContextMenu | null }
  | { type: 'SET_MAP_DISPATCH_MENU'; value: MapDispatchMenu | null }
  | { type: 'SET_TRIGGER_CARD'; value: TriggerCard | null }
  | { type: 'SET_CURSOR_COORDS'; value: string | null }
  | { type: 'SET_HOVERED_DRONE'; value: HoveredDrone | null }
  | { type: 'TOGGLE_TRAIL'; droneId: string }
  | { type: 'REMOVE_TRAIL'; droneId: string }
  | { type: 'SET_CONTROLLER_LINK'; value: string | null }
  | { type: 'TOGGLE_FOLLOW'; droneId: string }
  | { type: 'CLEAR_FOLLOW' }
  | { type: 'SET_MAP_ROTATED'; value: boolean }
  | { type: 'TICK_STALENESS'; nowMs: number }
  | { type: 'TOGGLE_AIRSPACE' }
  | { type: 'TOGGLE_MISSIONS' }
  | { type: 'TOGGLE_ALTITUDE_LABELS' }
  | { type: 'TOGGLE_RANGE_RINGS' }
  | { type: 'SET_BASEMAP_OFFLINE'; value: boolean }
  | { type: 'SET_BASEMAP_RETRYING'; value: boolean }
  | { type: 'AIRSPACE_LOAD_START' }
  | { type: 'AIRSPACE_LOAD_SUCCESS'; tfrItems: AirspaceTfrItem[]; notamItems: AirspaceNotamItem[]; notamClearToFly: boolean }
  | { type: 'AIRSPACE_LOAD_ERROR'; error: string }
  | { type: 'CLOSE_MENUS' }
  | { type: 'CLEAR_MAP_SELECTION' };

export const initialMapUiState: MapUiState = {
  isFullscreen: false,
  // The server's basemap, replaced by the initializer's localStorage read on the
  // client. Both renders must agree, so this is the one style that needs no store.
  mapStyle: FALLBACK_MAP_STYLE,
  contextMenu: null,
  mapDispatchMenu: null,
  triggerCard: null,
  cursorCoords: null,
  hoveredDrone: null,
  trailDroneIds: new Set(),
  controllerLinkId: null,
  followDroneId: null,
  stalenessTick: 0,
  stalenessNowMs: 0,
  mapRotated: false,
  showAirspace: false,
  showMissions: false,
  showAltitudeLabels: false,
  showRangeRings: true,
  basemapOffline: false,
  basemapRetrying: false,
  tfrItems: [],
  notamItems: [],
  notamClearToFly: true,
  loadingAirspace: false,
  airspaceErr: null,
};

export function mapUiReducer(state: MapUiState, action: MapUiAction): MapUiState {
  switch (action.type) {
    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.value };
    case 'SET_MAP_STYLE':
      return { ...state, mapStyle: action.value };
    case 'SET_CONTEXT_MENU':
      return { ...state, contextMenu: action.value, mapDispatchMenu: action.value ? null : state.mapDispatchMenu, triggerCard: action.value ? null : state.triggerCard };
    case 'SET_MAP_DISPATCH_MENU':
      return { ...state, mapDispatchMenu: action.value, contextMenu: action.value ? null : state.contextMenu, triggerCard: action.value ? null : state.triggerCard };
    // Mutually exclusive with the other two, the same way they are with each
    // other: two cards stacked at one click point is never what was meant.
    case 'SET_TRIGGER_CARD':
      return { ...state, triggerCard: action.value, contextMenu: action.value ? null : state.contextMenu, mapDispatchMenu: action.value ? null : state.mapDispatchMenu };
    case 'SET_CURSOR_COORDS':
      return { ...state, cursorCoords: action.value };
    case 'SET_HOVERED_DRONE': {
      const next = action.value;
      const prev = state.hoveredDrone;
      // mousemove fires continuously; bail out when nothing actually changed so
      // a stationary cursor over a marker does not re-render the map shell on
      // every event.
      if (next === null && prev === null) return state;
      if (
        next && prev
        && next.droneId === prev.droneId
        && next.x === prev.x
        && next.y === prev.y
        && next.containerWidth === prev.containerWidth
      ) {
        return state;
      }
      return { ...state, hoveredDrone: next };
    }
    case 'TOGGLE_TRAIL': {
      const next = new Set(state.trailDroneIds);
      if (next.has(action.droneId)) next.delete(action.droneId);
      else next.add(action.droneId);
      return { ...state, trailDroneIds: next };
    }
    case 'REMOVE_TRAIL': {
      const next = new Set(state.trailDroneIds);
      next.delete(action.droneId);
      return { ...state, trailDroneIds: next };
    }
    case 'SET_CONTROLLER_LINK':
      return { ...state, controllerLinkId: action.value };
    case 'TOGGLE_FOLLOW':
      return {
        ...state,
        followDroneId: state.followDroneId === action.droneId ? null : action.droneId,
      };
    case 'CLEAR_FOLLOW':
      return { ...state, followDroneId: null };
    case 'SET_MAP_ROTATED':
      // Bail out when nothing moved. `rotate` fires on every frame of a drag on
      // the compass, and this is read by a control column that re-renders the
      // map key with it.
      return state.mapRotated === action.value ? state : { ...state, mapRotated: action.value };
    case 'TICK_STALENESS':
      return { ...state, stalenessTick: state.stalenessTick + 1, stalenessNowMs: action.nowMs };
    case 'TOGGLE_AIRSPACE':
      return { ...state, showAirspace: !state.showAirspace };
    case 'TOGGLE_MISSIONS':
      return { ...state, showMissions: !state.showMissions };
    case 'TOGGLE_ALTITUDE_LABELS':
      return { ...state, showAltitudeLabels: !state.showAltitudeLabels };
    case 'TOGGLE_RANGE_RINGS':
      return { ...state, showRangeRings: !state.showRangeRings };
    case 'SET_BASEMAP_OFFLINE':
      // A basemap that is back online is no longer retrying, so clearing the
      // flag here keeps the two from disagreeing (a stuck "retrying" label on
      // a map that already recovered).
      return { ...state, basemapOffline: action.value, basemapRetrying: false };
    case 'SET_BASEMAP_RETRYING':
      return { ...state, basemapRetrying: action.value };
    case 'AIRSPACE_LOAD_START':
      return { ...state, loadingAirspace: true, airspaceErr: null };
    case 'AIRSPACE_LOAD_SUCCESS':
      return {
        ...state,
        loadingAirspace: false,
        tfrItems: action.tfrItems,
        notamItems: action.notamItems,
        notamClearToFly: action.notamClearToFly,
      };
    case 'AIRSPACE_LOAD_ERROR':
      return { ...state, loadingAirspace: false, airspaceErr: action.error };
    case 'CLOSE_MENUS':
      return { ...state, contextMenu: null, mapDispatchMenu: null, triggerCard: null };
    case 'CLEAR_MAP_SELECTION':
      return { ...state, controllerLinkId: null, followDroneId: null, contextMenu: null, mapDispatchMenu: null, triggerCard: null };
    default:
      return state;
  }
}
